import { type GridSnapLiveRect, snapPointToNearestGridCorner } from '@/lib/guides/grid-snap'
import type { ObjectBounds } from '@/lib/guides/types/object-bounds'
import { type SmartGuideLine, computeSmartGuides } from '@/lib/guides/smart-guides'
import { snapToAngle45 } from '@/components/app/editor/tools/shared/helpers/snap-to-angle-45'

/** The overlay's snap-related props, read fresh at each pointer event. */
export interface PenSnapSettings {
  snapToGrid: boolean
  gridSnapStep?: number
  gridSnapLiveRects?: GridSnapLiveRect[]
  smartGuidesEnabled: boolean
  snapGuideDistance: number
  getShapeBoundsForSmartGuides?: () => ObjectBounds[]
}

function maybeSnapToGrid(
  x: number,
  y: number,
  settings: PenSnapSettings
): { x: number; y: number } {
  const step = settings.gridSnapStep
  const rects = settings.gridSnapLiveRects

  if (!settings.snapToGrid || !step || !rects?.length) return { x, y }
  return snapPointToNearestGridCorner(x, y, step, rects)
}

function computePenSmartGuideSnap(
  x: number,
  y: number,
  settings: PenSnapSettings
): { x: number; y: number; guides: SmartGuideLine[] } {
  if (!settings.smartGuidesEnabled) return { x, y, guides: [] }
  const bounds = settings.getShapeBoundsForSmartGuides?.()

  if (!bounds?.length) return { x, y, guides: [] }
  const threshold = settings.snapGuideDistance
  const result = computeSmartGuides({ x, y, width: 0, height: 0 }, bounds, threshold)

  return { x: x + result.snapAdjust.dx, y: y + result.snapAdjust.dy, guides: result.guides }
}

/**
 * Snap pipeline for a pen-tool cursor position. With an `angleOrigin` (Shift held, measured from
 * the last anchor) the point snaps to the nearest 45° from it and smart guides are disabled;
 * otherwise it snaps to the grid, then to smart guides. Returns the guides to publish.
 */
export function resolvePenSnap(
  x: number,
  y: number,
  angleOrigin: { x: number; y: number } | null,
  settings: PenSnapSettings
): { x: number; y: number; guides: SmartGuideLine[] } {
  if (angleOrigin) {
    return { ...snapToAngle45(angleOrigin.x, angleOrigin.y, x, y), guides: [] }
  }

  const gridded = maybeSnapToGrid(x, y, settings)

  return computePenSmartGuideSnap(gridded.x, gridded.y, settings)
}
